// IAS-owned Redux slice. Mounted under `ias` in `reducers-main.ts` via a single
// hook line; the `State` interface is extended below via TypeScript declaration
// merging so upstream's `metabase/redux/store/state` module doesn't need to know
// about us.
//
// Kept small on purpose — anything IAS-specific that must be openable/closable
// from anywhere in the app (session-expired modal, settings-loaded gate, etc.)
// lives here. Component-local state stays local.

import { type PayloadAction, createSlice } from "@reduxjs/toolkit";

export interface IasState {
  isSessionExpiredModalOpen: boolean;
  isAppSettingsLoaded: boolean;
}

const initialState: IasState = {
  isSessionExpiredModalOpen: false,
  isAppSettingsLoaded: false,
};

const iasSlice = createSlice({
  name: "ias",
  initialState,
  reducers: {
    openSessionExpired(state) {
      state.isSessionExpiredModalOpen = true;
    },
    closeSessionExpired(state) {
      state.isSessionExpiredModalOpen = false;
    },
    appSettingsLoaded(state, action: PayloadAction<boolean | undefined>) {
      state.isAppSettingsLoaded = action.payload ?? true;
    },
  },
});

export const iasReducer = iasSlice.reducer;
export const { openSessionExpired, closeSessionExpired, appSettingsLoaded } =
  iasSlice.actions;

// Selectors — routed through the augmented `State` (see below) so consumers get
// the correct type without importing `IasState` directly.
export const getIsSessionExpiredModalOpen = (state: { ias: IasState }) =>
  state.ias.isSessionExpiredModalOpen;

export const getIsAppSettingsLoaded = (state: { ias: IasState }) =>
  state.ias.isAppSettingsLoaded;

// Extend upstream's root `State` type with the `ias` slice via declaration
// merging so `useSelector` in IAS components is properly typed without editing
// upstream files.
declare module "metabase/redux/store/state" {
  interface State {
    ias: IasState;
  }
}
